import React, { useEffect, useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';

export interface FacetOption {
  value: string;
  label?: string;
}

interface Props {
  label: string;
  options: Array<FacetOption | string>;
  selected: string[];
  onChange: (_next: string[]) => void;
  /** Optional icon shown before the label (e.g. a Columns icon). */
  startIcon?: React.ReactNode;
  align?: 'left' | 'right';
}

/**
 * A themed multi-select dropdown used for each filter facet and the columns
 * picker. Custom (not MUI Menu) so it fully respects the app's CSS-variable
 * theming rather than the global white MuiPaper override.
 */
const FacetSelect: React.FC<Props> = ({
  label,
  options,
  selected,
  onChange,
  startIcon,
  align = 'left',
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const opts: FacetOption[] = options.map((o) =>
    typeof o === 'string' ? { value: o } : o,
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const toggle = (v: string) => {
    onChange(
      selected.includes(v)
        ? selected.filter((x) => x !== v)
        : [...selected, v],
    );
  };

  return (
    <div className='eh-facet' ref={ref}>
      <button
        type='button'
        className={`eh-facet-btn ${selected.length ? 'active' : ''} ${
          open ? 'open' : ''
        }`}
        onClick={() => setOpen((o) => !o)}
      >
        {startIcon}
        {label}
        {selected.length > 0 && (
          <span className='eh-facet-count'>{selected.length}</span>
        )}
        <ExpandMoreIcon style={{ fontSize: 16 }} />
      </button>
      {open && (
        <div
          className={`eh-facet-menu ${align === 'right' ? 'align-right' : ''}`}
        >
          {selected.length > 0 && (
            <button
              type='button'
              className='eh-facet-clear'
              onClick={() => onChange([])}
            >
              Clear selection
            </button>
          )}
          {opts.length === 0 && (
            <div className='eh-facet-empty'>No options</div>
          )}
          {opts.map((o) => {
            const on = selected.includes(o.value);
            return (
              <button
                key={o.value}
                type='button'
                className='eh-facet-option'
                onClick={() => toggle(o.value)}
              >
                <span className={`eh-facet-check ${on ? 'on' : ''}`}>
                  {on && <CheckIcon style={{ fontSize: 13 }} />}
                </span>
                {o.label || o.value}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FacetSelect;
